import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { useToast } from '../../../components/ui/Toast';

export interface CodeSnippetProps {
  /** Kod do wyświetlenia + do skopiowania. */
  code: string;
  /** Opcjonalna etykieta języka w rogu (tsx, jsx, bash, css). */
  language?: string;
  className?: string;
}

/**
 * Blok kodu z przyciskiem Copy w prawym górnym rogu. Używany w /branding
 * przy przykładach użycia komponentów - łatwo skopiować snippet do swojego
 * pliku albo do promptu AI.
 */
export function CodeSnippet({ code, language, className }: CodeSnippetProps) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success('Skopiowano snippet');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Nie udało się skopiować');
    }
  };

  return (
    <div className={['relative group', className].filter(Boolean).join(' ')}>
      {language && (
        <div className="absolute top-2 left-3 text-[10px] font-mono uppercase tracking-wider text-text-disabled pointer-events-none">
          {language}
        </div>
      )}
      <pre className="text-[11px] font-mono bg-surface-base/50 border border-border-subtle rounded-lg p-3 pr-12 overflow-x-auto text-text-secondary leading-relaxed">
        {code}
      </pre>
      <button
        type="button"
        onClick={handleCopy}
        className="absolute top-2 right-2 inline-flex items-center justify-center w-7 h-7 rounded-md bg-surface-raised/80 border border-border text-text-muted hover:text-accent-primary hover:border-accent-primary/40 hover:bg-surface-raised transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
        aria-label="Skopiuj kod"
        title="Skopiuj"
      >
        {copied ? <Check className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}
